import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HeartIcon, AwardIcon, StarIcon, DollarSignIcon, BuildingIcon, UsersIcon, CheckCircleIcon, ClipboardCheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const DonorRecognitionPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const topIndividualDonors = [{
    name: 'Dr. Eleanor Chen',
    tier: 'Visionary',
    years: '2021-Present',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "The Ukweli Institute's commitment to scientific integrity in the wellness space inspired me to support their mission. Their work is transforming how we approach health and wellness products."
  }, {
    name: 'Michael & Sarah Rodriguez',
    tier: 'Champion',
    years: '2020-Present',
    image: 'https://images.unsplash.com/photo-1531747118685-ca8fa6e08806?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: 'We believe that everyone deserves access to evidence-based information about products that affect their health. The Ukweli Institute is making that possible.'
  }, {
    name: 'James Wilson',
    tier: 'Visionary',
    years: '2022-Present',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "As someone who has seen the harm that can come from unsubstantiated health claims, I'm proud to support an organization that upholds scientific integrity."
  }, {
    name: 'Dr. Amara Patel',
    tier: 'Champion',
    years: '2021-Present',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: 'The Ukweli Institute represents the gold standard in microbiome research and consumer education. Their work is essential for advancing this field.'
  }, {
    name: 'Robert & Linda Thompson',
    tier: 'Advocate',
    years: '2020-Present',
    image: 'https://images.unsplash.com/photo-1515463892140-58a22e37ff72?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "We've been supporting the Ukweli Institute since its founding because we believe in the power of science to improve lives."
  }, {
    name: 'Thomas Lee',
    tier: 'Advocate',
    years: '2022-Present',
    image: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "The transparency and rigor that the Ukweli Institute brings to wellness research is unmatched. I'm honored to support their mission."
  }];
  const corporatePartners = [{
    name: 'HealthTech Innovations',
    tier: 'Platinum Partner',
    years: '2020-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Supporting research on digital health technologies and microbiome science'
  }, {
    name: 'NutriWell Labs',
    tier: 'Gold Partner',
    years: '2021-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Funding research on supplement efficacy and safety standards'
  }, {
    name: 'BioPure Solutions',
    tier: 'Gold Partner',
    years: '2022-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Supporting educational initiatives on microbiome health'
  }, {
    name: 'Global Wellness Foundation',
    tier: 'Silver Partner',
    years: '2021-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Funding research on international wellness standards'
  }];
  const donorImpact = [{
    title: 'Research Advancement',
    description: 'Donor support has enabled 15 new research studies on microbiome science and wellness product efficacy in the past year alone.',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-696" />
  }, {
    title: 'Educational Resources',
    description: "We've developed 30+ evidence-based educational resources that have reached over 50,000 consumers and healthcare providers.",
    icon: <UsersIcon size={24} className="text-blue-600" data-id="element-697" />
  }, {
    title: 'Certification Standards',
    description: 'Our donor-funded certification program has evaluated over 200 products, helping consumers identify truly effective options.',
    icon: <CheckCircleIcon size={24} className="text-blue-600" data-id="element-698" />
  }, {
    title: 'Global Outreach',
    description: 'Donor support has helped us expand our research and educational initiatives to 5 new countries in the past year.',
    icon: <BuildingIcon size={24} className="text-blue-600" data-id="element-699" />
  }];
  return <div className="bg-white w-full" data-id="element-700">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702">
          <div className="text-center" data-id="element-703">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-704">
              Donor Recognition
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-705">
              Honoring those who make our mission possible through their
              generous support
            </p>
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="py-16 bg-white" data-id="element-706">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-707">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-708">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-709">
              Thank You to Our Supporters
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-710">
              The Ukweli Institute's work is made possible through the generous
              support of individuals, foundations, and corporate partners who
              share our commitment to advancing evidence-based science in the
              wellness industry. We are deeply grateful for their contributions.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Individual Donors Section */}
      <section className="py-16 bg-gray-50" data-id="element-711">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-712">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-713">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-714">
              <HeartIcon size={28} data-id="element-715" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-716">
              Individual Donors
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-717">
              We recognize the following individuals for their exceptional
              commitment to our mission through their generous financial
              support.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-718">
            {topIndividualDonors.map((donor, index) => <motion.div key={donor.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-719">
                <div className="p-6" data-id="element-720">
                  <div className="flex items-center mb-4" data-id="element-721">
                    <img src={donor.image} alt={donor.name} className="h-16 w-16 rounded-full object-cover mr-4" onError={e => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80';
                }} data-id="element-722" />
                    <div data-id="element-723">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-724">
                        {donor.name}
                      </h3>
                      <div className="flex items-center" data-id="element-725">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-726">
                          {donor.tier}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-727">
                          {donor.years}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 italic" data-id="element-728">"{donor.quote}"</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Corporate Partners Section */}
      <section className="py-16 bg-white" data-id="element-729">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-730">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-731">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-732">
              <BuildingIcon size={28} data-id="element-733" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-734">
              Corporate & Foundation Partners
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-735">
              We are grateful to the following organizations for their
              significant support of our research and educational initiatives.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-736">
            {corporatePartners.map((partner, index) => <motion.div key={partner.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-gray-50 rounded-lg p-6 shadow-sm" data-id="element-737">
                <div className="flex flex-col md:flex-row items-center" data-id="element-738">
                  <div className="flex-shrink-0 w-24 h-24 bg-white rounded-lg flex items-center justify-center mb-4 md:mb-0 md:mr-6" data-id="element-739">
                    <img src={partner.logo} alt={partner.name} className="max-h-16 max-w-full" onError={e => {
                  e.currentTarget.src = 'https://via.placeholder.com/150/e2e8f0/1e40af?text=Logo';
                }} data-id="element-740" />
                  </div>
                  <div data-id="element-741">
                    <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-742">
                      {partner.name}
                    </h3>
                    <div className="flex flex-wrap items-center mb-2" data-id="element-743">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-744">
                        {partner.tier}
                      </span>
                      <span className="text-sm text-gray-600" data-id="element-745">
                        {partner.years}
                      </span>
                    </div>
                    <p className="text-gray-700" data-id="element-746">{partner.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Impact Section */}
      <section className="py-16 bg-gray-50" data-id="element-747">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-748">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-749">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-750">
              <StarIcon size={28} data-id="element-751" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-752">
              Donor Impact
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-753">
              Our donors' generous support has enabled significant progress in
              our mission to advance evidence-based science in the wellness
              industry.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-754">
            {donorImpact.map((impact, index) => <motion.div key={impact.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-755">
                <div className="flex items-start" data-id="element-756">
                  <div className="flex-shrink-0 mr-4" data-id="element-757">
                    <div className="bg-blue-50 rounded-full p-3" data-id="element-758">
                      {impact.icon}
                    </div>
                  </div>
                  <div data-id="element-759">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-760">
                      {impact.title}
                    </h3>
                    <p className="text-gray-700" data-id="element-761">{impact.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Legacy Giving Section */}
      <section className="py-16 bg-white" data-id="element-762">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-763">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-764">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-765">
              <AwardIcon size={28} data-id="element-766" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-767">
              Legacy Society
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-768">
              We honor those who have included the Ukweli Institute in their
              estate plans, ensuring our work continues for generations to come.
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-769">
            <div className="text-center mb-8" data-id="element-770">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-771">
                The Ukweli Legacy Society Members
              </h3>
              <p className="text-gray-700" data-id="element-772">
                We gratefully acknowledge the following individuals who have
                made provisions for the Ukweli Institute in their estate plans:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-center" data-id="element-773">
              <div className="p-4" data-id="element-774">
                <p className="font-medium text-gray-900" data-id="element-775">
                  Dr. Margaret Williams
                </p>
              </div>
              <div className="p-4" data-id="element-776">
                <p className="font-medium text-gray-900" data-id="element-777">
                  John & Patricia Anderson
                </p>
              </div>
              <div className="p-4" data-id="element-778">
                <p className="font-medium text-gray-900" data-id="element-779">Richard Lee, PhD</p>
              </div>
              <div className="p-4" data-id="element-780">
                <p className="font-medium text-gray-900" data-id="element-781">Elizabeth Chen</p>
              </div>
              <div className="p-4" data-id="element-782">
                <p className="font-medium text-gray-900" data-id="element-783">
                  David & Susan Thompson
                </p>
              </div>
              <div className="p-4" data-id="element-784">
                <p className="font-medium text-gray-900" data-id="element-785">Anonymous (3)</p>
              </div>
            </div>
            <div className="mt-8 text-center" data-id="element-786">
              <p className="text-gray-700 mb-4" data-id="element-787">
                To learn more about including the Ukweli Institute in your
                estate plans, please contact our Development Office.
              </p>
              <Link to="/contact" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 transition-colors duration-200" data-id="element-788">
                Contact Us About Legacy Giving
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-50" data-id="element-789">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-790">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-791">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-792">
              Join Our Community of Supporters
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-793">
              Your support can help advance evidence-based science and empower
              consumers with trustworthy information. Consider making a donation
              today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-794">
              <Link to="/support-us" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center" data-id="element-795">
                <DollarSignIcon size={18} className="mr-2" data-id="element-796" />
                Make a Donation
              </Link>
              <Link to="/contact" className="px-6 py-3 bg-blue-700 text-white font-medium rounded-md shadow-md hover:bg-blue-800 transition-colors duration-300 flex items-center justify-center" data-id="element-797">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};